import React from 'react';
import { IntlShape } from 'react-intl';
import getAreaNameById from '../../../utils/getAreaNameById';
import getFleetNameById from '../../../utils/getFleetNameById';
import CarStatus from '../../shared/CarStatus';
import { CarLike } from '../types';

// Optional left columns offered by the timeline's Columns selector (plate is
// always-on and rendered specially in PlateColumn). Keys match CarsTable
// `dataIndex`; values reuse the SAME derivations as CarTable/getColumns.tsx and
// the SAME header text keys — so the timeline mirrors CarsTable.
export type TimelineColumnKey =
  | 'status'
  | 'areaId'
  | 'fleetId'
  | 'model'
  | 'chassisNumber'
  | 'comingBookings';

// Registry display order (matches ColumnSelector/timeTableColumns).
export const TIMELINE_COLUMN_ORDER: TimelineColumnKey[] = [
  'status',
  'areaId',
  'fleetId',
  'model',
  'chassisNumber',
  'comingBookings',
];

export type ColumnCtx = {
  areas: unknown;
  fleets: unknown;
  intl: IntlShape;
  notAssignedLabel: string;
  // carId -> count of upcoming (future-starting) rental bookings.
  comingBookingsByCar?: Map<string, number>;
};

export type TimelineColumnDef = {
  header: string; // EN fallback for headerTextId; same wording as CarsTable's column title
  headerTextId: string;
  getValue: (car: CarLike, ctx: ColumnCtx) => React.ReactNode;
  // Sortable primitive for table-style header sorting (mirrors getValue's
  // derivation but returns a string/number so rows can be ordered).
  getSortValue: (car: CarLike, ctx: ColumnCtx) => string | number;
};

// --- Table-style sort over the sticky-left columns ('plate' + the extras). ---
export type SortKey = 'plate' | TimelineColumnKey;
export type SortDirection = 'asc' | 'desc';
export type TimelineSort = { key: SortKey; direction: SortDirection } | null;

// Compact status badge sizing (same recipe used elsewhere in the 44px row).
const STATUS_BADGE_STYLE = {
  fontSize: 10,
  padding: '0 6px',
  borderRadius: 8,
} as const;

export const TIMELINE_COLUMN_DEFS: Record<
  TimelineColumnKey,
  TimelineColumnDef
> = {
  status: {
    header: 'Status',
    headerTextId: 'fleetadmin.car.table.status',
    getValue: (car) =>
      car.status ? (
        <CarStatus status={car.status} style={STATUS_BADGE_STYLE} />
      ) : (
        '—'
      ),
    getSortValue: (car) => car.status || '',
  },
  areaId: {
    header: 'Area',
    headerTextId: 'fleetadmin.car.table.area',
    getValue: (car, ctx) =>
      car.areaId
        ? String(getAreaNameById(ctx.areas as any, car.areaId))
        : ctx.notAssignedLabel,
    getSortValue: (car, ctx) =>
      car.areaId
        ? String(getAreaNameById(ctx.areas as any, car.areaId))
        : ctx.notAssignedLabel,
  },
  fleetId: {
    header: 'Fleet',
    headerTextId: 'fleetadmin.car.table.fleet',
    getValue: (car, ctx) =>
      car.fleetId
        ? String(getFleetNameById(ctx.fleets as any, car.fleetId))
        : '—',
    getSortValue: (car, ctx) =>
      car.fleetId
        ? String(getFleetNameById(ctx.fleets as any, car.fleetId))
        : '',
  },
  model: {
    header: 'Model',
    headerTextId: 'fleetadmin.car.table.model',
    getValue: (car) => [car.brand, car.model].filter(Boolean).join(' ') || '—',
    getSortValue: (car) => [car.brand, car.model].filter(Boolean).join(' '),
  },
  chassisNumber: {
    header: 'Vehicle VIN',
    headerTextId: 'fleetadmin.car.table.vin',
    getValue: (car) => car.chassisNumber || '—',
    getSortValue: (car) => car.chassisNumber || '',
  },
  comingBookings: {
    header: '# of coming bookings',
    headerTextId: 'fleetadmin.timeline.comingbookings',
    getValue: (car, ctx) => ctx.comingBookingsByCar?.get(car.id) ?? 0,
    getSortValue: (car, ctx) => ctx.comingBookingsByCar?.get(car.id) ?? 0,
  },
};

// Resolve a row's sortable value for a given column key ('plate' is special-cased
// since it isn't part of the optional-column registry).
export const getColumnSortValue = (
  key: SortKey,
  car: CarLike,
  ctx: ColumnCtx
): string | number =>
  key === 'plate'
    ? car.plate || ''
    : TIMELINE_COLUMN_DEFS[key].getSortValue(car, ctx);

// Mixed comparator: numeric when both sides are numbers, otherwise a
// case-insensitive locale compare (so text columns sort naturally).
export const compareSortValues = (
  a: string | number,
  b: string | number
): number =>
  typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).toLowerCase().localeCompare(String(b).toLowerCase());

// Filter+order a raw selected-keys array (from displayColumns.timeTable) down to
// the known optional columns, dropping 'plate' and anything unrecognised.
export const orderedExtraColumns = (
  selected: string[] | null | undefined
): TimelineColumnKey[] =>
  TIMELINE_COLUMN_ORDER.filter((k) => (selected ?? []).includes(k));
